import React, { useState, useEffect } from 'react';
import { Sun, Moon, Clock, ArrowLeft, Sunrise, Sunset, Star, Sparkles, AlertTriangle, ShieldAlert, Award, Compass } from 'lucide-react';
import { Link } from 'react-router-dom';
import RelatedTools from '../components/RelatedTools';
import axios from 'axios';
import Header from '../components/Header';
import FloatingActions from '../components/FloatingActions';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useLang } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DailyPanchangPage() {
  const [panchang, setPanchang] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const { lang } = useLang();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPanchang();
  }, []);

  const fetchPanchang = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/api/panchang`, { timeout: 20000 });
      if (res.data && res.data.tithi) {
        setPanchang(res.data);
      } else {
        setError('empty');
      }
    } catch (err) {
      console.error('Panchang fetch error:', err);
      setError(err.response?.status === 520 || err.code === 'ECONNABORTED' ? 'server' : 'network');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setRetryCount((c) => c + 1);
    fetchPanchang();
  };

  const today = new Date();
  const dateStr = today.toLocaleDateString(lang === 'hi' ? 'hi-IN' : 'en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const Stat = ({ icon: Icon, label, value, accent = 'gold', sub }) => (
    <div className="glass-card p-5 flex items-start gap-4" data-testid={`panchang-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className={`w-10 h-10 rounded-xl ${accent === 'gold' ? 'bg-gold/10' : accent === 'red' ? 'bg-red-500/15' : 'bg-primary/10'} flex items-center justify-center shrink-0`}>
        <Icon className={`w-5 h-5 ${accent === 'gold' ? 'text-gold' : accent === 'red' ? 'text-red-400' : 'text-primary'}`} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-text-muted uppercase tracking-wider">{label}</p>
        <p className="text-text-main font-medium text-lg break-words">{value || '—'}</p>
        {sub && <p className="text-xs text-text-muted/80 mt-0.5">{sub}</p>}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={lang === 'hi'
          ? 'आज का पंचांग — दैनिक तिथि, नक्षत्र, राहु काल'
          : "Today's Panchang — Daily Tithi, Nakshatra, Rahu Kaal"}
        breadcrumbs={[
          { name: lang === 'hi' ? 'होम' : 'Home', path: '/' },
          { name: lang === 'hi' ? 'पंचांग' : 'Panchang', path: '/panchang' },
        ]}
        description={lang === 'hi'
          ? 'आज का पंचांग — तिथि, नक्षत्र, योग, करण, राहु काल, सूर्योदय-सूर्यास्त, संवत्सर, अभिजित मुहूर्त। फरीदाबाद पंचांग by Acharya Vivek Mudgal — Swiss Ephemeris (Lahiri ayanamsa) सटीक।'
          : "Today's Panchang — Tithi, Nakshatra, Yoga, Karana, Rahu Kaal, Sunrise, Sunset, Samvatsara, Abhijit Muhurat. Faridabad Panchang by Acharya Vivek Mudgal (Swiss Ephemeris, Lahiri ayanamsa)."}
        path="/daily-panchang"
        keywords="daily panchang, आज का पंचांग, today panchang, tithi, nakshatra, yoga, karana, rahu kaal, abhijit muhurat, samvatsara, vikram samvat, faridabad panchang"
      />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-10 cosmic-bg">
        <div className="container-custom text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-primary hover:gap-3 transition-all mb-6 text-sm" data-testid="back-home-btn">
            <ArrowLeft className="w-4 h-4" /> {lang === 'hi' ? 'होम' : 'Home'}
          </Link>
          <span className="block text-xs tracking-[0.3em] uppercase text-gold font-semibold mb-3">
            {lang === 'hi' ? 'दैनिक पंचांग' : 'DAILY PANCHANG'}
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-text-main mb-3">
            {lang === 'hi' ? 'आज का पंचांग' : "Today's Panchang"}
          </h1>
          <p className="text-lg text-gold mb-2">{dateStr}</p>
          <p className="text-text-muted text-sm max-w-2xl mx-auto">
            {lang === 'hi'
              ? 'फरीदाबाद, हरियाणा — Swiss Ephemeris (लाहिरी अयनांश) से सटीक गणना'
              : 'Faridabad, Haryana — High-precision (Swiss Ephemeris, Lahiri ayanamsa)'}
          </p>
          <div className="divine-divider mt-7 max-w-xs mx-auto" />
        </div>
      </section>

      {/* Panchang Body */}
      <section className="section-padding pt-6">
        <div className="container-custom max-w-5xl">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin mb-3" />
              <p className="text-text-muted">{lang === 'hi' ? 'पंचांग गणना हो रही है... कृपया कुछ क्षण रुकें' : 'Computing today\'s Panchang... please wait'}</p>
            </div>
          ) : (!panchang || error) ? (
            <div className="text-center py-12 max-w-lg mx-auto" data-testid="panchang-error">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-500/10 border border-amber-500/30 mb-4">
                <Sun className="w-7 h-7 text-amber-400" />
              </div>
              <h3 className="font-heading text-2xl text-text-main mb-2">
                {lang === 'hi' ? 'पंचांग अभी load नहीं हो पाया' : 'Panchang did not load just now'}
              </h3>
              <p className="text-text-muted text-sm mb-5 leading-relaxed">
                {error === 'server'
                  ? (lang === 'hi'
                    ? 'सर्वर में अल्प-कालिक तकनीकी विलंब है। कृपया कुछ क्षण में फिर प्रयास करें।'
                    : 'There is a brief technical delay on the server. Please try again in a moment.')
                  : (lang === 'hi'
                    ? 'Network slow है या कुछ क्षणिक समस्या है — एक बार और try करें।'
                    : 'Network slow or temporary issue — please try again.')}
              </p>
              <button
                onClick={handleRetry}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-gold to-amber-500 text-purple-950 font-bold text-sm shadow-lg hover:scale-105 active:scale-95 transition-transform"
                data-testid="panchang-retry-btn">
                <Clock className="w-4 h-4" />
                {lang === 'hi' ? 'पुनः प्रयास करें' : 'Try Again'}
                {retryCount > 0 && <span className="text-[10px] opacity-70">({retryCount})</span>}
              </button>
            </div>
          ) : (
            <>
              {/* === Hindu Calendar Banner === */}
              <div className="glass-card p-6 mb-8 border border-gold/25" data-testid="panchang-samvat-banner">
                <div className="flex items-center gap-2 mb-3">
                  <Compass className="w-5 h-5 text-gold" />
                  <h2 className="font-heading text-xl text-text-main">
                    {lang === 'hi' ? 'हिन्दू पंचांग वर्ष' : 'Hindu Calendar Year'}
                  </h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-text-muted">{lang === 'hi' ? 'विक्रम संवत' : 'Vikram Samvat'}</p>
                    <p className="text-gold font-bold text-lg">{panchang.vikram_samvat}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-text-muted">{lang === 'hi' ? 'शक संवत' : 'Shaka Samvat'}</p>
                    <p className="text-gold font-bold text-lg">{panchang.shaka_samvat}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-text-muted">{lang === 'hi' ? 'संवत्सर' : 'Samvatsara'}</p>
                    <p className="text-text-main font-medium">
                      {panchang.samvatsara}
                      {panchang.samvatsara_number && (
                        <span className="text-gold/70 text-xs ml-1">({panchang.samvatsara_number}वाँ)</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-text-muted">{lang === 'hi' ? 'मास' : 'Masa'}</p>
                    <p className="text-text-main font-medium">{panchang.masa}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-text-muted">{lang === 'hi' ? 'ऋतु' : 'Ritu'}</p>
                    <p className="text-text-main font-medium">{panchang.ritu}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-text-muted">{lang === 'hi' ? 'अयन' : 'Ayana'}</p>
                    <p className="text-text-main font-medium">{panchang.ayana}</p>
                  </div>
                </div>
              </div>

              {/* === Main 5-anga Grid === */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                <Stat icon={Moon} label={lang === 'hi' ? 'तिथि' : 'Tithi'}
                      value={`${panchang.tithi} (${panchang.paksha})`}
                      sub={panchang.tithi_end_time ? `${lang === 'hi' ? 'समाप्त' : 'ends'} ${panchang.tithi_end_time} → ${panchang.next_tithi}` : null} />
                <Stat icon={Star} label={lang === 'hi' ? 'नक्षत्र' : 'Nakshatra'}
                      value={panchang.nakshatra} accent="primary"
                      sub={panchang.nakshatra_end_time ? `${lang === 'hi' ? 'समाप्त' : 'ends'} ${panchang.nakshatra_end_time} → ${panchang.next_nakshatra}` : null} />
                <Stat icon={Sparkles} label={lang === 'hi' ? 'योग' : 'Yoga'}
                      value={panchang.yoga}
                      sub={panchang.yoga_end_time ? `${lang === 'hi' ? 'समाप्त' : 'ends'} ${panchang.yoga_end_time} → ${panchang.next_yoga}` : null} />
                <Stat icon={Clock} label={lang === 'hi' ? 'करण' : 'Karana'} value={panchang.karana} accent="primary" />
                <Stat icon={Sun} label={lang === 'hi' ? 'वार' : 'Vaar'} value={`${panchang.day} (${panchang.day_lord})`} />
                <Stat icon={Award} label={lang === 'hi' ? 'अभिजित मुहूर्त' : 'Abhijit Muhurat'}
                      value={panchang.abhijit_muhurat}
                      sub={lang === 'hi' ? 'सबसे शुभ समय' : 'Most auspicious window'} />
              </div>

              {/* === Sun/Moon Times === */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <Stat icon={Sunrise} label={lang === 'hi' ? 'सूर्योदय' : 'Sunrise'} value={panchang.sunrise} />
                <Stat icon={Sunset} label={lang === 'hi' ? 'सूर्यास्त' : 'Sunset'} value={panchang.sunset} accent="primary" />
                <Stat icon={Moon} label={lang === 'hi' ? 'चंद्रोदय' : 'Moonrise'} value={panchang.moonrise} />
                <Stat icon={Moon} label={lang === 'hi' ? 'चंद्रास्त' : 'Moonset'} value={panchang.moonset} accent="primary" />
              </div>

              {/* === Inauspicious Windows === */}
              <div className="grid sm:grid-cols-3 gap-4 mb-8">
                <div className="glass-card p-5 border-red-500/25" data-testid="panchang-rahu-kaal">
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldAlert className="w-5 h-5 text-red-400" />
                    <h3 className="text-red-400 font-heading text-lg">{lang === 'hi' ? 'राहु काल' : 'Rahu Kaal'}</h3>
                  </div>
                  <p className="text-text-main text-2xl font-medium">{panchang.rahu_kaal || '—'}</p>
                  <p className="text-xs text-text-muted mt-2">{lang === 'hi' ? 'शुभ कार्य न करें' : 'Avoid auspicious work'}</p>
                </div>
                <div className="glass-card p-5 border-amber-500/25" data-testid="panchang-yamaganda">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-amber-400" />
                    <h3 className="text-amber-400 font-heading text-lg">{lang === 'hi' ? 'यमगण्ड' : 'Yamaganda'}</h3>
                  </div>
                  <p className="text-text-main text-2xl font-medium">{panchang.yamaganda || '—'}</p>
                  <p className="text-xs text-text-muted mt-2">{lang === 'hi' ? 'यात्रा/नया काम बचें' : 'Avoid travel/new ventures'}</p>
                </div>
                <div className="glass-card p-5 border-purple-500/25" data-testid="panchang-gulika">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-primary" />
                    <h3 className="text-primary font-heading text-lg">{lang === 'hi' ? 'गुलिक काल' : 'Gulika Kaal'}</h3>
                  </div>
                  <p className="text-text-main text-2xl font-medium">{panchang.gulika || '—'}</p>
                  <p className="text-xs text-text-muted mt-2">{lang === 'hi' ? 'पुनरावृत्ति कर्म से बचें' : 'Avoid recurring acts'}</p>
                </div>
              </div>

              {/* === Planetary Rashis === */}
              <div className="glass-card p-6 mb-8">
                <h3 className="font-heading text-xl text-text-main mb-4">
                  {lang === 'hi' ? 'ग्रह स्थिति (सायन/Lahiri Ayanamsa)' : 'Planetary Positions (Sidereal/Lahiri)'}
                </h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="flex justify-between py-2 border-b border-white/5">
                    <span className="text-text-muted">{lang === 'hi' ? 'सूर्य राशि' : 'Sun Sign'}</span>
                    <span className="text-gold">{panchang.sun_rashi}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-white/5">
                    <span className="text-text-muted">{lang === 'hi' ? 'चंद्र राशि' : 'Moon Sign'}</span>
                    <span className="text-gold">{panchang.moon_rashi}</span>
                  </div>
                </div>
              </div>

              {/* === CTAs === */}
              <div className="text-center mt-6">
                <div className="flex flex-wrap justify-center gap-3">
                  <Link to="/daily-rashifal" className="btn-gold" data-testid="go-rashifal-btn">
                    {lang === 'hi' ? 'आज का राशिफल देखें' : 'View Daily Horoscope'}
                  </Link>
                  <a href="/#contact" className="btn-gold-outline" data-testid="consult-panchang-cta">
                    {lang === 'hi' ? 'मुहूर्त परामर्श लें' : 'Get Muhurat Consultation'}
                  </a>
                </div>
                <p className="text-[11px] text-text-muted/70 mt-4 italic">
                  {lang === 'hi'
                    ? 'गणना: Swiss Ephemeris (NASA-grade) + पराशर ज्योतिष परंपरा, Lahiri अयनांश'
                    : 'Computed with Swiss Ephemeris (NASA-grade) + Parashar tradition, Lahiri ayanamsa'}
                </p>
              </div>
            </>
          )}
        </div>
      </section>
      <FloatingActions />

      <RelatedTools />
      <Footer />
    </div>
  );
}
